import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { showToast } from '../utils/toast';
import { FaTimes, FaPaperclip, FaPlus, FaTrash, FaEdit, FaEye, FaExchangeAlt } from 'react-icons/fa';
import Split from 'react-split';
import Shipments from './Shipments';
import OrderItemsPicker from '../components/OrderItemsPicker';
import './EOrderItems.css';
const MIN_COL_W = 40;
const API = import.meta.env.VITE_API_URL;
const emptyLine = () => ({ sku: '', qty: 1, description: '' });
const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const formatUnitValue = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? currencyFormatter.format(n) : (Number.isFinite(n) && n === 0 ? currencyFormatter.format(0) : '');
};

/* =========================
   Image helpers (robust)
   ========================= */
const isDataUrl = (s) => typeof s === 'string' && s.startsWith('data:image/');
const isHttpUrl = (s) => typeof s === 'string' && (/^https?:\/\//i.test(s) || s.startsWith('/'));
const isB64 = (s) => typeof s === 'string' && s.length > 64 && /^[A-Za-z0-9+/=\r\n]+$/.test(s) && !/\s/.test(s);
const makeBlobUrl = (arrLike, mime = 'image/jpeg', bag) => {
  try {
    const u8 = arrLike instanceof Uint8Array ? arrLike : new Uint8Array(Array.from(arrLike || []));
    const blob = new Blob([u8], { type: mime });
    const url = URL.createObjectURL(blob);
    bag.current.add(url);
    return url;
  } catch {
    return '';
  }
};
const resolveItemThumb = (item, bag) => {
  const urlFields = ['itemimageurl', 'item_image_url', 'imageurl', 'image_url', 'thumb', 'thumbnail'];
  for (const f of urlFields) {
    const v = item?.[f];
    if (v && (isHttpUrl(v) || isDataUrl(v))) return v;
  }
  const baseFields = ['image1', 'itemimage1', 'image', 'img'];
  for (const f of baseFields) {
    const v = item?.[f];
    if (!v) continue;
    if (typeof v === 'string') {
      if (isDataUrl(v)) return v.trim();
      if (isB64(v)) return `data:image/jpeg;base64,${v.trim()}`;
      if (v.trim().startsWith('data:image/')) return v.trim();
    }
    if (v && typeof v === 'object') {
      if (v.type === 'Buffer' && Array.isArray(v.data)) return makeBlobUrl(v.data, 'image/jpeg', bag);
      if (Array.isArray(v)) return makeBlobUrl(v, 'image/jpeg', bag);
      if (Array.isArray(v?.data)) return makeBlobUrl(v.data, 'image/jpeg', bag);
    }
  }
  return '';
};

// User full name for history logs
function getUserFullName() {
  try {
    const u = JSON.parse(localStorage.getItem('currentUser')) || JSON.parse(localStorage.getItem('user')) || {};
    return u.fullname || u.userFullName || u.fullName || u.name || u.login || '';
  } catch {
    return '';
  }
}

// Lightweight client-role detector (mirrors logic used elsewhere)
function isClientUser() {
  try {
    const u = JSON.parse(localStorage.getItem('currentUser')) || JSON.parse(localStorage.getItem('user')) || {};
    const flag = u.isclient ?? u.isClient ?? u.client ?? null;
    if (flag === true || flag === 1 || flag === '1') return true;
    const roleStr = [u.role, u.userrole, u.type, u.usertype]
      .filter(Boolean)
      .map(v => String(v).toLowerCase())
      .join(' ');
    return roleStr.includes('client');
  } catch { return false; }
}

// Lightweight admin-role detector (kept inline in useMemo below to avoid HMR scoping issues)

export default function EOrderItems({ eorderId, clientName, orderNumber, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Soft-delete view removed; always show non-deleted items
  const [selectedIds, setSelectedIds] = useState(new Set());
  const IS_ADMIN = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem('currentUser')) || JSON.parse(localStorage.getItem('user')) || {};
      return u?.isadmin === 1 || u?.isadmin === true || String(u?.isadmin) === '1';
    } catch { return false; }
  }, []);
  // Only rows with no shipments are selectable (admins can select any row)
  const selectableIds = useMemo(() => (
    IS_ADMIN ? (rows || []).map(r => r.id) : (rows || []).filter(r => Number(r?.qty_shipped || 0) === 0).map(r => r.id)
  ), [rows, IS_ADMIN]);
  const allSelected = useMemo(() => (
    selectableIds.length > 0 && selectableIds.every(id => selectedIds.has(id))
  ), [selectableIds, selectedIds]);
  const [activeItemId, setActiveItemId] = useState(null);
  const [activeFOLId, setActiveFOLId] = useState(null);
  const [sizes, setSizes] = useState([100, 0]);
  const isOpen = sizes[1] > 0;
  const [preview, setPreview] = useState({ src: '', x: 0, y: 0, visible: false });
  const previewRef = useRef(null);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const blobUrlBag = useRef(new Set());
  const revokeAllBlobUrls = () => {
    for (const u of blobUrlBag.current) URL.revokeObjectURL(u);
    blobUrlBag.current.clear();
  };
  const [editQtyOpen, setEditQtyOpen] = useState(false);
  const [editQtyRows, setEditQtyRows] = useState([]);
  const [editQtyValues, setEditQtyValues] = useState({});
  const [editQtyReason, setEditQtyReason] = useState('');
  const [editQtyBusy, setEditQtyBusy] = useState(false);
  const [editQtyError, setEditQtyError] = useState('');

  // Header / permissions
  const [clientId, setClientId] = useState('');
  const [orderStatusName, setOrderStatusName] = useState(''); // e.g., NEW, PACKING, READY TO SHIP, BACK ORDER
  const IS_CLIENT = useMemo(() => isClientUser(), []);

  // Resizable columns for E-Order Items
  // Reordered: move fulfillment_status after image; drop fulfillment_location column
  const EOI_COLS = ['select','actions','image','fulfillment_status','qty_ordered','qty_allocated','qty_to_ship','qty_shipped','sku','itemdescription','areaname','location','storageunit','receiptid','palletnumber','unit_value','weight_oz','weight_lbs','length','width','height'];
  const LS_EOI_COLS = 'eorderItemsColWidths.v1';
  const getInitialEoiWidths = () => {
    const defaults = { select: 40, actions: 72, image: 72 };
    try {
      const raw = localStorage.getItem(LS_EOI_COLS);
      const j = raw ? JSON.parse(raw) : {};
      return j && typeof j === 'object' ? { ...defaults, ...j } : defaults;
    } catch {
      return defaults;
    }
  };
  const [eoiColWidths, setEoiColWidths] = useState(getInitialEoiWidths());
  const eoiHeaderRef = useRef(null);
  const eoiTableRef = useRef(null);
  const eoiResizeRef = useRef({ key:null, startX:0, startW:0 });
  useEffect(()=>{ try{ localStorage.setItem(LS_EOI_COLS, JSON.stringify(eoiColWidths)); }catch{} }, [eoiColWidths]);

  const visibleEoiCols = useMemo(() => (
    IS_CLIENT ? EOI_COLS.filter((col) => col !== 'actions') : EOI_COLS
  ), [IS_CLIENT]);

  const ensureEoiWidthsLocked = useCallback(() => {
    const row = eoiHeaderRef.current;
    if (!row) return;
    const missing = visibleEoiCols.some((key) => eoiColWidths[key] == null);
    if (!missing) return;
    const next = {};
    visibleEoiCols.forEach((key, idx) => {
      const th = row.children?.[idx];
      if (th) next[key] = th.offsetWidth;
    });
    if (Object.keys(next).length) {
      setEoiColWidths((prev) => ({ ...prev, ...next }));
    }
  }, [visibleEoiCols, eoiColWidths]);
  const onEoiMove = (e)=>{ const {key,startX,startW}=eoiResizeRef.current; if(!key) return; const nextW=Math.max(MIN_COL_W,startW+(e.clientX-startX)); setEoiColWidths(prev=>({...prev,[key]:nextW})); e.preventDefault(); };
  const onEoiUp = ()=>{ eoiResizeRef.current={ key:null,startX:0,startW:0 }; try{document.body.classList.remove('col-resizing');}catch{} window.removeEventListener('mousemove', onEoiMove); window.removeEventListener('mouseup', onEoiUp); };
  const startEoiResize = (key,e)=>{ ensureEoiWidthsLocked(); const th=e.currentTarget.parentElement; eoiResizeRef.current={ key, startX:e.clientX, startW: th.offsetWidth }; try{document.body.classList.add('col-resizing');}catch{} window.addEventListener('mousemove', onEoiMove); window.addEventListener('mouseup', onEoiUp); e.preventDefault(); e.stopPropagation(); };
  const autoFitEoiCol = useCallback((key) => {
    try {
      const idx = visibleEoiCols.indexOf(key);
      if (idx === -1) return;
      let maxW = 0;
      const hdr = eoiHeaderRef.current?.children?.[idx];
      if (hdr) maxW = Math.max(maxW, Math.ceil(hdr.scrollWidth));
      const tbl = eoiTableRef.current;
      if (tbl?.tBodies?.[0]) {
        const rows = tbl.tBodies[0].rows;
        for (let r = 0; r < rows.length; r += 1) {
          const cell = rows[r].children[idx];
          if (cell) maxW = Math.max(maxW, Math.ceil(cell.scrollWidth));
        }
      }
      const target = Math.max(MIN_COL_W, Math.min(800, maxW));
      setEoiColWidths((prev) => ({ ...prev, [key]: target }));
    } catch {}
  }, [visibleEoiCols]);

  // Replace SKU modal state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [swapOpen, setSwapOpen] = useState(false);
  const [swapFor, setSwapFor] = useState(null);
  const [skuQ, setSkuQ] = useState('');
  const [skuLoading, setSkuLoading] = useState(false);
  const [skuResults, setSkuResults] = useState([]);
  const [skuSelected, setSkuSelected] = useState(null);
  const [swapErr, setSwapErr] = useState('');

  // --- Add Label (manual) modal state ---
  const [labelOpen, setLabelOpen] = useState(false);
  const [labelFiles, setLabelFiles] = useState([]);
  const labelInputRef = useRef(null);
  const [labelCarrier, setLabelCarrier] = useState('UPS');
  const [labelTracking, setLabelTracking] = useState(''); // default/fallback
  const [labelTracks, setLabelTracks] = useState({});     // per-file tracking map: key -> string
  const [labelBusy, setLabelBusy] = useState(false);

  /* ---------- Data ---------- */
  const fetchJSON = async (url, opts) => {
    const res = await fetch(url, opts);
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try {
        const b = await res.json();
        if (b?.error) msg = b.error;
      } catch {}
      throw new Error(msg);
    }
    return res.json();
  };

  const load = async () => {
    if (!eorderId) return;
    setLoading(true);
    setError('');
    try {
      // Load items
      const q = new URLSearchParams();
      q.set('deleted', '0');
      const data = await fetchJSON(`${API}/api/eorders/${eorderId}/items?${q.toString()}`);
      revokeAllBlobUrls();
      setRows(Array.isArray(data) ? data : []);
      setSelectedIds(new Set());
      setActiveItemId(null);
      setActiveFOLId(null);
      setSizes([100, 0]);

      // Load header for client + status (used for permissions)
      try {
        const h = await fetchJSON(`${API}/api/eorders/${eorderId}`);
        const cid = String(h?.clientid ?? h?.clientId ?? '');
        setClientId(cid);
        setOrderStatusName(String(h?.ourstatusname || h?.our_status_name || '').toUpperCase());
      } catch {
        setClientId('');
        setOrderStatusName('');
      }
    } catch (e) {
      setRows([]);
      setError(e.message || 'Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    return revokeAllBlobUrls;
  }, [eorderId]);

  const loadHeaderForClient = async () => {
    try {
      const h = await fetchJSON(`${API}/api/eorders/${eorderId}`);
      const cid = String(h?.clientid ?? h?.clientId ?? '');
      setClientId(cid);
      setOrderStatusName(String(h?.ourstatusname || h?.our_status_name || '').toUpperCase());
      return cid;
    } catch {
      setClientId('');
      setOrderStatusName('');
      return '';
    }
  };

  /* ---------- Permissions: can replace SKU? ---------- */
  // Allowed: NEW, REQUESTED, PICKING, PICKED, PACKING OR BACK ORDER
  const canReplaceSku = useMemo(() => {
    const s = (orderStatusName || '').toUpperCase();
    if (!s) return true; // default to allow if unknown
    const allowed = new Set(['NEW','REQUESTED','PICKING','PICKED','PACKING','BACK ORDER']);
    return allowed.has(s);
  }, [orderStatusName]);

  // Client users: only allow Replace SKU when status is exactly NEW
  const canReplaceSkuForUser = useMemo(() => {
    if (!IS_CLIENT) return canReplaceSku;
    return String(orderStatusName || '').toUpperCase() === 'NEW';
  }, [IS_CLIENT, orderStatusName, canReplaceSku]);

  // Freeze operations when status is READY TO SHIP, SHIPPED, or DELIVERED
  const isFrozen = useMemo(() => {
    const s = (orderStatusName || '').toUpperCase();
    return s === 'READY TO SHIP' || s === 'SHIPPED' || s === 'DELIVERED';
  }, [orderStatusName]);

  // For client users, restrict destructive actions unless order is NEW
  const clientActionsDisabled = useMemo(() => {
    if (!IS_CLIENT) return false;
    return String(orderStatusName || '').toUpperCase() !== 'NEW';
  }, [IS_CLIENT, orderStatusName]);

  /* ---------- Helpers ---------- */
  const pad4 = (n) => (n || n === 0 ? String(n).padStart(4, '0') : '');
  const fmtLocation = (r) => {
    if (r?.locationtypeid === 1 || (!r?.roww && !r?.columnn && !r?.shelve && !r?.bin)) return 'FLOOR';
    return [r?.roww, r?.columnn, r?.shelve, r?.bin].filter(Boolean).join('-') || '';
  };

  // Compute effective on-hand pieces for display in Replace SKU results.
  // Experimental: only trust piecesonhand; do not infer from qtyunitsonhand * qtyperunit.
  const computeOnHandPieces = (r) => {
    const pieces = Number(r?.piecesonhand ?? 0);
    return Number.isFinite(pieces) && pieces > 0 ? pieces : 0;
  };

  // Format a human-friendly on-hand string showing pieces and units (when available)
  const formatOnHand = (r) => {
    const pieces = computeOnHandPieces(r);
    const units = Number(r?.qtyunitsonhand ?? 0);
    const showUnits = Number.isFinite(units) && units > 0 && pieces > 0;
    return showUnits ? `${pieces} pcs (${units} units)` : `${pieces} pcs`;
  };

  // Map numeric location type to label for Replace SKU results (1=Floor, 2=Rack, 3|6=Picking)
  const fmtLocationType = (r) => {
    const t = r?.locationtype ?? r?.locationtypeid;
    const n = Number(t);
    if (Number.isFinite(n)) {
      if (n === 6 || n === 3) return 'PICKING'; // handle both schemas
      if (n === 2) return 'RACK';
      if (n === 1) return 'FLOOR';
    }
    const s = String(t ?? '').trim();
    if (s === '6' || s === '3') return 'PICKING';
    if (s === '2') return 'RACK';
    if (s === '1') return 'FLOOR';
    // Fallback: infer from location label when type missing
    const loc = String(r?.location || '').toUpperCase();
    if (loc.startsWith('FLOOR')) return 'FLOOR';
    return s ? s : '';
  };

  const placePreview = (clientX, clientY) => {
    const vw = window.innerWidth, vh = window.innerHeight;
    const margin = 12, offset = 16;
    const boxW = previewRef.current?.offsetWidth || Math.min(vw * 0.4, 520);
    const boxH = previewRef.current?.offsetHeight || Math.min(vh * 0.6, 520);
    let left = clientX + offset, top = clientY + offset;
    if (left + boxW + margin > vw) left = clientX - boxW - offset;
    if (top + boxH + margin > vh) top = clientY - boxH - offset;
    if (left < margin) left = margin;
    if (top < margin) top = margin;
    if (left + boxW + margin > vw) left = vw - boxW - margin;
    if (top + boxH + margin > vh) top = vh - boxH - margin;
    setPreview(p => ({ ...p, x: left, y: top }));
  };

  const showPreview = (raw, e) => {
    const src = resolveItemThumb({ image1: raw, itemimageurl: raw }, blobUrlBag);
    if (!src) return;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    setPreview({ src, x: e.clientX, y: e.clientY, visible: true });
    requestAnimationFrame(() => placePreview(e.clientX, e.clientY));
  };

  const movePreview = (e) => {
    if (!preview.visible) return;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    placePreview(e.clientX, e.clientY);
  };

  const hidePreview = () => setPreview(p => ({ ...p, visible: false }));
  const onPreviewImgLoad = () => {
    if (!preview.visible) return;
    const { x, y } = lastPosRef.current;
    placePreview(x, y);
  };

  /* ---------- Selection ---------- */
  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  const toggleSelectAllOnPage = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(selectableIds));
  };

  const selectedArray = useMemo(() => Array.from(selectedIds), [selectedIds]);

  /* ---------- Items Soft-Delete / Restore ---------- */
  const postAction = async (path, body = {}) => {
    const res = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    let data = {};
    try {
      data = await res.json();
    } catch {}
    if (!res.ok) throw new Error(data?.error || 'Request failed');
    return data;
  };

  const softDeleteItems = async () => {
    if (!selectedArray.length) return alert('Select at least one line.');
    try {
      await postAction(`${API}/api/eorders/${eorderId}/items/soft-delete`, { itemIds: selectedArray });
      await load();
    } catch (err) {
      alert(err.message);
    }
  };

  const restoreItems = async () => {
    if (!selectedArray.length) return alert('Select at least one line.');
    try {
      await postAction(`${API}/api/eorders/${eorderId}/items/restore`, { itemIds: selectedArray });
      await load();
    } catch (err) {
      alert(err.message);
    }
  };

  /* ---------- Bulk Edit Quantities ---------- */
  const openEditQtyModal = () => {
    if (!IS_ADMIN) return;
    if (!selectedArray.length) {
      alert('Select at least one line to edit.');
      return;
    }
    const uniqIds = Array.from(new Set(selectedArray));
    const mapped = uniqIds
      .map(id => rows.find(r => r.id === id))
      .filter(Boolean);
    const editable = mapped.filter(r => Number(r?.qty_shipped || 0) === 0);
    if (!editable.length) {
      alert('Selected lines cannot be edited because they already have shipped quantity.');
      return;
    }
    const defaults = {};
    editable.forEach(r => { defaults[r.id] = String(r.qty_ordered ?? ''); });
    setEditQtyRows(editable);
    setEditQtyValues(defaults);
    setEditQtyReason('');
    setEditQtyError('');
    setEditQtyOpen(true);
  };

  const closeEditQtyModal = () => {
    if (editQtyBusy) return;
    setEditQtyOpen(false);
    setEditQtyRows([]);
    setEditQtyValues({});
    setEditQtyReason('');
    setEditQtyError('');
  };

  const saveEditedQty = async () => {
    if (!editQtyRows.length) {
      setEditQtyError('No rows selected.');
      return;
    }
    const payload = editQtyRows.map(r => ({
      lineId: r.id,
      qtyOrdered: Number(editQtyValues[r.id])
    }));
    const invalid = payload.some(p => !Number.isFinite(p.qtyOrdered) || p.qtyOrdered < 0);
    if (invalid) {
      setEditQtyError('Quantities must be non-negative numbers.');
      return;
    }
    if (!editQtyReason.trim()) {
      setEditQtyError('Please enter a reason for the quantity change.');
      return;
    }
    setEditQtyBusy(true);
    setEditQtyError('');
    try {
      const res = await fetch(`${API}/api/eorders/${eorderId}/items/bulk-update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: payload, reason: editQtyReason })
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j?.error) throw new Error(j?.error || 'Failed to update items');
      if (Array.isArray(j?.errors) && j.errors.length) {
        alert(j.errors.join('\n'));
      }
      showToast({
        id: 'eorders.items.editqty',
        message: `Updated ${payload.length} line${payload.length === 1 ? '' : 's'}.`,
        duration: 2200,
        align: 'center',
        slideFrom: 'top',
        pill: true
      });
      setEditQtyOpen(false);
      setSelectedIds(new Set());
      await load();
    } catch (err) {
      setEditQtyError(err?.message || 'Failed to update line items');
    } finally {
      setEditQtyBusy(false);
    }
  };

  /* ---------- Inventory Picker (Deducts) ---------- */
  const openPicker = async () => {
    await loadHeaderForClient();
    setPickerOpen(true);
  };

  const fetchAvailable = async (filters) => {
    const params = new URLSearchParams();
    params.set('clientid', clientId || '');
    ['q', 'sku', 'lot', 'pallet', 'area', 'location', 'receiptid', 'limit', 'offset'].forEach(k => {
      const v = filters?.[k];
      if (v !== undefined && v !== null && String(v) !== '') params.set(k, v);
    });
    const url = `${API}/api/inventory/available${params.toString() ? `?${params.toString()}` : ''}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  };

  const persistPicks = async (rowsPicked) => {
    if (!eorderId || !Array.isArray(rowsPicked) || !rowsPicked.length) {
      return { success: false, added: 0 };
    }
    const items = rowsPicked.map(r => ({
      inventoryid: Number(r.inventoryid),
      boxes: Math.max(0, Number(r.boxes || 0)),
      pieces: Math.max(0, Number(r.pieces || 0)),
    })).filter(x => x.inventoryid && (x.boxes > 0 || x.pieces > 0));
    if (!items.length) return { success: false, added: 0 };
    const r = await fetch(`${API}/api/eorders/${eorderId}/items/allocate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: getUserFullName(), items })
    });
    return r.json();
  };

  /* ---------- Replace SKU Flow ---------- */
  const openSkuSwap = async (line) => {
    // Guard: disallow based on role-sensitive rule
    if (!canReplaceSkuForUser) {
      // optional UX hint
      alert(IS_CLIENT
        ? 'Clients can only change SKU when order status is NEW.'
        : 'Replacing SKUs is disabled once the order moves past PACKING (except BACK ORDER).'
      );
      return;
    }
    const cid = clientId || (await loadHeaderForClient());
    if (!cid) {
      alert('Unable to resolve client for this order.');
      return;
    }
    setSwapFor(line);
    setSkuQ(line.sku || '');
    setSkuSelected(null);
    setSwapErr('');
    setSkuResults([]);
    setSwapOpen(true);
    setTimeout(() => {
      document.querySelector('.sku-searchbar input')?.focus();
    }, 0);
  };

  /* ---------- Open shipments pane for a specific line ---------- */
  const openShipments = (line) => {
    try {
      const folRaw = Number(line?.fulfillment_order_line_id || 0);
      const fol = folRaw && folRaw > 0 ? folRaw : null; // only filter if we have a real FO line id
      setActiveItemId(line?.id || null);
      setActiveFOLId(fol);
      setSizes([65, 35]);
    } catch {}
  };

  const runSkuSearch = async (term) => {
    try {
      setSkuLoading(true);
      const params = new URLSearchParams();
      if (clientId) params.set('clientid', clientId);
      if (term) params.set('q', term);
      params.set('limit', '500');
      params.set('offset', '0');
      const url = `${API}/api/inventory/available?${params.toString()}`;
      const res = await fetch(url);
      const j = await res.json().catch(() => ({ data: [] }));
      const arr = Array.isArray(j?.data) ? j.data : (Array.isArray(j) ? j : []);
      // Experimental: for Replace SKU, treat only rows with piecesonhand>0 as truly available
      const filtered = arr.filter(r => Number(r?.piecesonhand ?? 0) > 0);
      setSkuResults(filtered);
    } catch (e) {
      setSkuResults([]);
    } finally {
      setSkuLoading(false);
    }
  };

  const confirmSkuSwap = async () => {
    if (!swapFor?.id || !skuSelected?.sku) {
      setSwapErr('Pick a SKU first.');
      return;
    }
    try {
      setSwapErr('');
      const body = {
        newSku: skuSelected.sku,
        inventoryid: skuSelected.inventoryid || undefined,
        returnAllocated: Number(swapFor.qty_allocated || 0) > 0
      };
      const res = await fetch(`${API}/api/eorders/${eorderId}/items/${swapFor.id}/replace-sku`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j?.success === false) throw new Error(j?.error || 'Replace SKU failed');
      setSwapOpen(false);
      setSwapFor(null);
      setSkuSelected(null);
      await load();
    } catch (e) {
      setSwapErr(e.message || 'Replace SKU failed');
    }
  };

  const cancelSkuSwap = () => {
    setSwapOpen(false);
    setSwapFor(null);
    setSkuSelected(null);
    setSwapErr('');
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && swapOpen) cancelSkuSwap();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [swapOpen]);

  /* ---------- Split Open/Close ---------- */
  const closeShipments = () => {
    setActiveItemId(null);
    setActiveFOLId(null);
    setSizes([100, 0]);
  };

  return (
    <div className="eoi-split-root" style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Split
        className="eoi-horizontal-split"
        direction="horizontal"
        sizes={sizes}
        minSize={[400, isOpen ? 320 : 0]}
        gutterSize={6}
        style={{ height: '100%' }}
        onDragEnd={(s) => setSizes(s)}
      >
        {/* LEFT PANE: Items */}
        <div className="pane" style={{ height: '100%', minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div className="eorder-items-root" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="eorder-items-header" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, flex: 1 }}>
                Items for {clientName ? `${clientName} — ` : ''}Order {orderNumber ? `#${orderNumber}` : ''}
              </h3>
              {/* Soft-delete view removed */}
            </div>
            <div className="eoi-toolbar">
              {(!isFrozen || IS_ADMIN) && (
                <>
                  <div className="left">
                    <label className="mini-check">
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAllOnPage} title="Select all on page" />
                      <span style={{ opacity: .9 }}>Select All</span>
                    </label>
                    {!!selectedIds.size && <span className="sel-count">{selectedIds.size} selected</span>}
                  </div>
                  <div className="right">
                    <button
                      className="btn primary"
                      onClick={openPicker}
                      title={clientActionsDisabled ? 'Disabled for clients unless order is NEW' : 'Deducts from inventory lines'}
                      disabled={clientActionsDisabled}
                    >
                      <FaPlus style={{ marginRight: 6 }} />Add From Inventory
                    </button>
                    {IS_ADMIN && (
                      <button
                        className="btn"
                        onClick={openEditQtyModal}
                        title="Edit ordered quantity"
                        disabled={!selectedArray.length}
                      >
                        <FaEdit style={{ marginRight: 6 }} />Edit Qty
                      </button>
                    )}
                    {/* Soft Delete/Restore removed */}
                    <button className="btn danger" disabled={clientActionsDisabled} onClick={async () => {
                      if (!selectedArray.length) return alert('Select at least one line.');
                      const ask = window.prompt(`Type DELETE to remove ${selectedArray.length} line(s) from this order.`);
                      if (ask !== 'DELETE') return;
                      try {
                        let userFullName = 'system';
                        try { userFullName = JSON.parse(localStorage.getItem('user')||'{}')?.fullname || 'system'; } catch {}
                        await postAction(`${API}/api/eorders/${eorderId}/items/remove`, { itemIds: selectedArray, userFullName });
                        await load();
                      } catch (e) { alert(e.message); }
                    }}>
                      <FaTrash style={{ marginRight: 6 }} />Remove From Order
                    </button>
                    <button className="btn" onClick={() => {
                      if (!selectedArray.length) return alert('Select at least one line.');
                      // Clear any pre‑existing staged files and tracking when opening
                      setLabelFiles([]);
                      setLabelTracks({});
                      setLabelTracking('');
                      try { if (labelInputRef.current) labelInputRef.current.value = ''; } catch {}
                      setLabelOpen(true);
                    }} title="Attach a manual label and create shipment(s)">
                      <FaPaperclip style={{ marginRight: 6 }} />Add Label
                    </button>
                  </div>
                </>
              )}
            </div>
            {loading ? (
              <div className="loading">Loading items…</div>
            ) : error ? (
              <div className="loading" style={{ color: '#ff7a7a' }}>{error}</div>
            ) : (
              <div className="table-scroll-wrapper" style={{ overflow: 'auto', minHeight: 0, flex: 1 }}>
                <table className="eorders-table" ref={eoiTableRef} style={{ background: 'transparent' }}>
                  <colgroup>
                    {visibleEoiCols.map(k => (
                      <col key={k} style={{ width: eoiColWidths[k] ? `${eoiColWidths[k]}px` : undefined }} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr ref={eoiHeaderRef}>
                      <th style={{ position: 'relative' }}>
                        {(!isFrozen || IS_ADMIN) && (
                          <input type="checkbox" checked={allSelected} onChange={toggleSelectAllOnPage} title="Select all on page" />
                        )}
                        <span className="col-resizer" onMouseDown={(e)=>startEoiResize('select', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEoiCol('select'); }} />
                      </th>
                      {!IS_CLIENT && (
                        <th style={{ position: 'relative', textAlign: 'center' }}>
                          Actions
                          <span className="col-resizer" onMouseDown={(e)=>startEoiResize('actions', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEoiCol('actions'); }} />
                        </th>
                      )}
                      <th style={{ position: 'relative' }}>Image
                        <span className="col-resizer" onMouseDown={(e)=>startEoiResize('image', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEoiCol('image'); }} />
                      </th>
                      <th style={{ position: 'relative' }}>Fulfillment Status
                        <span className="col-resizer" onMouseDown={(e)=>startEoiResize('fulfillment_status', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEoiCol('fulfillment_status'); }} />
                      </th>
                      <th style={{ position: 'relative' }}>Qty Ordered
                        <span className="col-resizer" onMouseDown={(e)=>startEoiResize('qty_ordered', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEoiCol('qty_ordered'); }} />
                      </th>
                      <th style={{ position: 'relative' }}>Allocated
                        <span className="col-resizer" onMouseDown={(e)=>startEoiResize('qty_allocated', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEoiCol('qty_allocated'); }} />
                      </th>
                      <th style={{ position: 'relative' }}>Qty To Ship
                        <span className="col-resizer" onMouseDown={(e)=>startEoiResize('qty_to_ship', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEoiCol('qty_to_ship'); }} />
                      </th>
                      <th style={{ position: 'relative' }}>Shipped
                        <span className="col-resizer" onMouseDown={(e)=>startEoiResize('qty_shipped', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEoiCol('qty_shipped'); }} />
                      </th>
                      <th style={{ position: 'relative' }}>SKU
                        <span className="col-resizer" onMouseDown={(e)=>startEoiResize('sku', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEoiCol('sku'); }} />
                      </th>
                      <th style={{ position: 'relative' }}>Description
                        <span className="col-resizer" onMouseDown={(e)=>startEoiResize('itemdescription', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEoiCol('itemdescription'); }} />
                      </th>
                      <th style={{ position: 'relative' }}>Area
                        <span className="col-resizer" onMouseDown={(e)=>startEoiResize('areaname', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEoiCol('areaname'); }} />
                      </th>
                      <th style={{ position: 'relative' }}>Location
                        <span className="col-resizer" onMouseDown={(e)=>startEoiResize('location', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEoiCol('location'); }} />
                      </th>
                      <th style={{ position: 'relative' }}>Storage Type
                        <span className="col-resizer" onMouseDown={(e)=>startEoiResize('storageunit', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEoiCol('storageunit'); }} />
                      </th>
                      <th style={{ position: 'relative' }}>Receipt #
                        <span className="col-resizer" onMouseDown={(e)=>startEoiResize('receiptid', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEoiCol('receiptid'); }} />
                      </th>
                      <th style={{ position: 'relative' }}>Pallet #
                        <span className="col-resizer" onMouseDown={(e)=>startEoiResize('palletnumber', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEoiCol('palletnumber'); }} />
                      </th>
                      <th style={{ position: 'relative' }}>Unit Value
                        <span className="col-resizer" onMouseDown={(e)=>startEoiResize('unit_value', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEoiCol('unit_value'); }} />
                      </th>
                      <th style={{ position: 'relative' }}>Weight Oz
                        <span className="col-resizer" onMouseDown={(e)=>startEoiResize('weight_oz', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEoiCol('weight_oz'); }} />
                      </th>
                      <th style={{ position: 'relative' }}>Weight Lbs
                        <span className="col-resizer" onMouseDown={(e)=>startEoiResize('weight_lbs', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEoiCol('weight_lbs'); }} />
                      </th>
                      <th style={{ position: 'relative' }}>Length
                        <span className="col-resizer" onMouseDown={(e)=>startEoiResize('length', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEoiCol('length'); }} />
                      </th>
                      <th style={{ position: 'relative' }}>Width
                        <span className="col-resizer" onMouseDown={(e)=>startEoiResize('width', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEoiCol('width'); }} />
                      </th>
                      <th style={{ position: 'relative' }}>Height
                        <span className="col-resizer" onMouseDown={(e)=>startEoiResize('height', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEoiCol('height'); }} />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length ? rows.map((item) => {
                      const checked = selectedIds.has(item.id);
                      const isSelectedRow = activeItemId === item.id;
                      const rowClasses = [
                        activeItemId ? (isSelectedRow ? 'selected' : 'dimmed') : ''
                      ].filter(Boolean).join(' ');
                      const thumbSrc = resolveItemThumb(item, blobUrlBag);

                      // Render SKU cell: link only when allowed
                      const skuCell = canReplaceSkuForUser ? (
                        <button
                          className="linklike"
                          onClick={(e) => { e.stopPropagation(); openSkuSwap(item); }}
                          title="Replace SKU"
                        >
                          {item.sku || '(no sku)'}
                        </button>
                      ) : (
                        <span title={IS_CLIENT ? 'Clients can only change SKU when status is NEW.' : 'SKU changes are disabled once the order moves past PACKING (except BACK ORDER).'}>
                          {item.sku || '(no sku)'}
                        </span>
                      );

                      return (
                        <tr
                          key={item.allocation_id ? `${item.id}-${item.allocation_id}` : `${item.id}-${item.internal_inventory_id || 0}`}
                          className={rowClasses}
                          aria-selected={isSelectedRow || undefined}
                          onClick={() => openShipments(item)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td style={{ textAlign: 'center' }}>
                            {(IS_ADMIN || (!isFrozen && Number(item.qty_shipped || 0) === 0)) && (
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleSelect(item.id)}
                                onClick={(e) => e.stopPropagation()}
                                title="Select line"
                              />
                            )}
                          </td>
                          {!IS_CLIENT && (
                            <td className="td-actions" style={{ textAlign: 'center' }}>
                              <div className="actions">
                                <button
                                  className="mini-btn"
                                  title="View shipments / labels"
                                  onClick={(e) => { e.stopPropagation(); openShipments(item); }}
                                >
                                  <FaEye size={14} />
                                </button>
                                <button
                                  className="mini-btn"
                                  title={canReplaceSkuForUser ? 'Replace SKU' : 'Replace SKU (disabled)'}
                                  onClick={(e) => { e.stopPropagation(); openSkuSwap(item); }}
                                  disabled={!canReplaceSkuForUser || Number(item.qty_shipped || 0) > 0}
                                >
                                  <FaExchangeAlt size={13} />
                                </button>
                              </div>
                            </td>
                          )}
                          <td>
                            {thumbSrc ? (
                              <img
                                src={thumbSrc}
                                alt={item.itemdescription || item.sku || 'thumbnail'}
                                className="eoi-thumb"
                                onMouseEnter={(e) => showPreview(item.itemimageurl || item.image1, e)}
                                onMouseMove={movePreview}
                                onMouseLeave={hidePreview}
                              />
                            ) : (
                              <span style={{ opacity: 0.6 }}>—</span>
                            )}
                          </td>
                          <td>{item.fulfillment_status || ''}</td>
                          <td>{item.qty_ordered ?? ''}</td>
                          <td>{item.qty_allocated ?? ''}</td>
                          <td>{item.qty_to_ship ?? ''}</td>
                          <td>{item.qty_shipped ?? ''}</td>
                          <td>{skuCell}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{item.itemdescription || ''}</td>
                          <td className="nowrap">{item.areaname || ''}</td>
                          <td className="nowrap">{fmtLocation(item)}</td>
                          <td className="nowrap">{item.storageunit || ''}</td>
                          <td>{item.receiptid ?? ''}</td>
                          <td>{pad4(item.palletnumber)}</td>
                          <td className="nowrap">{formatUnitValue(item.unit_value)}</td>
                          <td>{item.weight_oz ?? ''}</td>
                          <td>{item.weight_lbs ?? ''}</td>
                          <td>{item.length ?? ''}</td>
                          <td>{item.width ?? ''}</td>
                          <td>{item.height ?? ''}</td>
                        </tr>
                      );
                    }) : (
                      <tr><td colSpan={visibleEoiCols.length} style={{ color: '#aaa' }}>No items</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            {preview.visible && preview.src && (
              <div className="eoi-preview" ref={previewRef} style={{ left: preview.x, top: preview.y }}>
                <img src={preview.src} alt="" onLoad={onPreviewImgLoad} />
              </div>
            )}
          </div>
        </div>
        {/* RIGHT PANE: Shipments */}
        <div className="pane" style={{ height: '100%', minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {isOpen ? (
            <Shipments
              eorderId={eorderId}
              folId={activeFOLId}
              onClose={closeShipments}
            />
          ) : null}
        </div>
      </Split>
      {/* Inventory Picker */}
      {pickerOpen && (
        <OrderItemsPicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onConfirm={async (rowsPicked) => {
            try {
              const result = await persistPicks(rowsPicked);
              if (result?.success) {
                await load();
                alert(`Added ${result.added ?? rowsPicked.length} line(s) to E-Order ${orderNumber ? `#${orderNumber}` : ''}.`);
              } else {
                alert(result?.error || 'Nothing to add.');
              }
            } catch (err) {
              alert(`Failed to add from inventory: ${err.message}`);
            } finally {
              setPickerOpen(false);
            }
          }}
          order={{
            orderid: eorderId ?? null,
            ordernum: orderNumber ?? eorderId ?? null,
            clientname: clientName ?? '',
            created: '',
            carrier: '',
          }}
          clientid={clientId ?? ''}
          warehouseid={''}
          fetchAvailable={fetchAvailable}
          persistPicks={persistPicks}
          allowBackorder={true}
          orderTypeLabel="E-Order"
          onBackorderAdd={() => {
            try {
              setPickerOpen(false);
              // refresh items list to reflect newly added backorder line
              load();
            } catch {}
          }}
        />
      )}

      {labelOpen && (
        <div className="label-modal-overlay">
          <div className="label-modal" role="dialog" aria-modal="true" aria-labelledby="addLabelTitle" onClick={(e)=>e.stopPropagation()}>
            <div className="label-head">
              <h3 id="addLabelTitle" className="label-title">Add Manual Shipping Label(s)</h3>
              <button className="mini-btn" onClick={() => !labelBusy && setLabelOpen(false)} aria-label="Close">×</button>
            </div>
            <div className="label-body">
              <div className="label-form-grid">
                <label className="span-2">
                  Label File(s) (PDF or PNG)
                  <input
                    ref={labelInputRef}
                    type="file"
                    multiple
                    accept="application/pdf,image/png,image/jpeg"
                    onChange={(e)=>{
                      const incoming = Array.from(e.target.files || []);
                      if (!incoming.length) return;
                      const currentTracking = (labelTracking || '').trim();
                      setLabelFiles(prev => {
                        const next = [...prev];
                        const nextTracks = { ...labelTracks };
                        for (const f of incoming) {
                          const key = `${f.name}|${f.size}|${f.lastModified}`;
                          const exists = next.some(x => `${x.name}|${x.size}|${x.lastModified}` === key);
                          if (!exists) {
                            next.push(f);
                            // Carry the currently entered tracking number onto this new file's row
                            if (!(key in nextTracks)) nextTracks[key] = currentTracking;
                          }
                        }
                        setLabelTracks(nextTracks);
                        return next;
                      });
                      // Clear the global tracking field so user can enter next tracking easily
                      setLabelTracking('');
                      // allow picking same file again if needed
                      try { if (labelInputRef.current) labelInputRef.current.value = ''; } catch {}
                    }}
                  />
                </label>
                <label>
                  Tracking Number
                  <input value={labelTracking} onChange={e=>setLabelTracking(e.target.value)} placeholder="1Z..., 94..., etc." />
                </label>
                <label>
                  Carrier
                  <select value={labelCarrier} onChange={e=>setLabelCarrier(e.target.value)}>
                    <option>UPS</option>
                    <option>FedEx</option>
                    <option>USPS</option>
                    <option>DHL</option>
                    <option>Other</option>
                  </select>
                </label>
                {labelFiles.length > 0 && (
                  <div className="span-2" style={{ marginTop: 8 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
                      {labelFiles.map((f, i) => {
                        const key = `${f.name}|${f.size}|${f.lastModified}`;
                        return (
                          <div key={`${key}-${i}`} style={{
                            display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center',
                            padding: '6px 8px', borderRadius: 8, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)'
                          }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 260px', gap: 8, alignItems: 'center' }}>
                              <span title={f.name} style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                              <input
                                type="text"
                                placeholder="Tracking # for this label"
                                value={labelTracks[key] ?? ''}
                                onChange={(e)=> setLabelTracks(prev => ({ ...prev, [key]: e.target.value }))}
                                style={{ height: 32 }}
                              />
                            </div>
                            <button
                              type="button"
                              className="chip-x"
                              title="Remove"
                              onClick={() => {
                                setLabelFiles(prev => prev.filter((_, idx) => idx !== i));
                                setLabelTracks(prev => { const n = { ...prev }; delete n[key]; return n; });
                              }}
                              style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: 18 }}
                            >×</button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              {!!selectedArray.length && (
                <div style={{ marginTop: 8, opacity: .85 }}>
                  Will attach to {selectedArray.length} selected line{selectedArray.length===1?'':'s'}.
                </div>
              )}
            </div>
            <div className="label-foot">
              <button className="btn" onClick={()=>setLabelOpen(false)} disabled={labelBusy} title="Close">
                <FaTimes style={{ marginRight: 6 }} />Cancel
              </button>
              <button className="btn primary" disabled={labelBusy || !labelFiles.length} onClick={async ()=>{
                if (!selectedArray.length) { alert('Select at least one line.'); return; }
                if (!labelFiles.length) { alert('Choose at least one label file.'); return; }
                setLabelBusy(true);
                try {
                  // Read all selected files to base64 payloads
                  const labelsBody = [];
                  for (const f of labelFiles) {
                    // Read file as base64 (strip data URL if present)
                    /* eslint-disable no-await-in-loop */
                    const b64 = await new Promise((resolve, reject) => {
                      const fr = new FileReader();
                      fr.onerror = () => reject(new Error('Failed to read file'));
                      fr.onload = () => {
                        const res = fr.result;
                        if (typeof res === 'string' && res.startsWith('data:')) {
                          resolve(res.split(',')[1] || '');
                        } else if (res instanceof ArrayBuffer) {
                          let binary=''; const bytes=new Uint8Array(res);
                          for (let i=0;i<bytes.length;i++) binary += String.fromCharCode(bytes[i]);
                          resolve(btoa(binary));
                        } else resolve('');
                      };
                      fr.readAsDataURL(f);
                    });
                    const isPdf = /pdf$/i.test(f.type) || /\.pdf$/i.test(f.name);
                    const key = `${f.name}|${f.size}|${f.lastModified}`;
                    labelsBody.push({
                      trackingNumber: (labelTracks[key] || labelTracking || '').trim() || null,
                      carrier: labelCarrier || null,
                      labelType: isPdf ? 1 : 2,
                      pdfBase64: isPdf ? b64 : undefined,
                      pngBase64: !isPdf ? b64 : undefined,
                    });
                  }
                  const storeRes = await fetch(`${API}/api/eorders/${eorderId}/labels`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ labels: labelsBody })
                  });
                  const stored = await storeRes.json().catch(()=>({}));
                  if (!storeRes.ok || stored?.success === false) throw new Error(stored?.error || 'Failed to store label');
                  const labelIds = (Array.isArray(stored?.results) ? stored.results.map(r=>r.id).filter(Boolean) : []);
                  if (!labelIds.length) throw new Error('Label stored but id missing');

                  // If multiple labels were added, create a simple packaging run with one package per label
                  let runId = null;
                  try {
                    const pkgCount = Math.max(1, labelIds.length);
                    const packages = Array.from({ length: pkgCount }, (_, i) => ({ package_no: i + 1, source: 'own', items: [] }));
                    const packRes = await fetch(`${API}/api/eorders/${eorderId}/packaging`, {
                      method: 'POST', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ created_by: getUserFullName() || 'system', note: 'Manual label attach', packages })
                    });
                    const packJ = await packRes.json().catch(()=>({}));
                    if (packRes.ok && packJ?.run_id) runId = Number(packJ.run_id) || null;
                  } catch (e) {
                    // Non-fatal; continue without packaging linkage
                    console.warn('Packaging run create failed:', e?.message || e);
                  }

                  // Create shipments for selected items (map each label to its own package number)
                  const itemById = new Map(rows.map(r => [r.id, r]));
                  const targetIds = (selectedArray.length > 1 && labelIds.length > 1)
                    ? [selectedArray[0]] // if multiple files + multiple lines, attach to first line only
                    : selectedArray;
                  const shipments = [];
                  let pkgCounter = 1;
                  for (const id of targetIds) {
                    const row = itemById.get(id) || {};
                    const qtyPer = labelIds.length > 1 ? 1 : (Number(row.qty_to_ship || 1) || 1);
                    // Robustly resolve fulfillment references from various item field names
                    const folResolved = Number(
                      row.fulfillment_order_line_id ??
                      row.fulfillment_order_line_item_id ??
                      row.fo_line_item_id ??
                      0
                    );
                    const fidResolved = Number(
                      row.fulfillment_id ??
                      row.fulfillment_order_id ??
                      row.fo_id ??
                      0
                    );
                    for (const lid of labelIds) {
                      shipments.push({
                        labelId: lid,
                        fulfillment_order_line_id: folResolved || 0,
                        fulfillment_id: fidResolved || 0,
                        eorder_line_item_id: Number(row.id || 0),
                        qty: qtyPer,
                        // Map each label to a distinct package number; if we created a run,
                        // the POST /shipments will link label↔package
                        package_no: pkgCounter++,
                      });
                    }
                  }
                  const shipRes = await fetch(`${API}/api/eorders/${eorderId}/shipments`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(runId ? { shipments, run_id: runId } : { shipments })
                  });
                  const shipJ = await shipRes.json().catch(()=>({}));
                  if (!shipRes.ok || shipJ?.success === false) {
                    const errMsg = shipJ?.error || (Array.isArray(shipJ?.errors) && shipJ.errors.map(e=>e.error||'').filter(Boolean).join('; ')) || 'Failed to create shipments';
                    throw new Error(errMsg);
                  }
                  // Fallback for schemas/workflows that prefer server-side creation: backfill any labels with no shipments
                  if (!Number(shipJ.inserted || 0)) {
                    try {
                      await fetch(`${API}/api/eorders/${eorderId}/shipments/backfill-labels`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ labelIds })
                      });
                    } catch {}
                  }

                  // Reload items
                  await load();

                  // After saving shipments, set order status based on label coverage/completeness
                  try {
                    const itemsRes = await fetch(`${API}/api/eorders/${eorderId}/items?deleted=0`);
                    const items = await itemsRes.json().catch(() => []);
                    const fullyShipped = Array.isArray(items) && items.length > 0 && items.every(r => Number(r?.qty_shipped || 0) >= Number(r?.qty_ordered || r?.qty || 0));
                    const metaRes = await fetch(`${API}/api/eorders/meta`);
                    const meta = await metaRes.json().catch(() => ({}));
                    const list = Array.isArray(meta?.ourstatuses) ? meta.ourstatuses : [];
                    const shippedId = (list.find(s => String(s.name || '').toUpperCase() === 'SHIPPED')?.id) ?? 7;
                    const requestedId = (list.find(s => String(s.name || '').toUpperCase() === 'REQUESTED')?.id) ?? 2;
                    const readyToShipId = (list.find(s => String(s.name || '').toUpperCase() === 'READY TO SHIP')?.id) ?? 6;
                    // Consider a line "has label" if it has any shipped quantity (non-zero),
                    // which we use as a proxy for having at least one label/shipment attached.
                    const allHaveLabels = Array.isArray(items) && items.length > 0 && items.every(r => Number(r?.qty_shipped || 0) > 0);
                    // If all lines have labels, prefer READY TO SHIP instead of SHIPPED.
                    const target = allHaveLabels ? readyToShipId : (fullyShipped ? shippedId : requestedId);
                    await fetch(`${API}/api/eorders/${eorderId}`, {
                      method: 'PUT', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ our_order_status: target })
                    });
                    try { window.dispatchEvent(new CustomEvent('eorders:refresh')); } catch {}
                  } catch (e) {
                    console.warn('Auto status update after labels failed:', e?.message || e);
                  }
                  // Open shipments pane to show attached labels for the primary target line
                  try {
                    const focusId = (selectedArray.length ? selectedArray[0] : 0) || 0;
                    const focusRow = itemById.get(focusId) || {};
                    setActiveItemId(focusId);
                    const folRaw = Number(
                      focusRow.fulfillment_order_line_id ??
                      focusRow.fulfillment_order_line_item_id ??
                      focusRow.fo_line_item_id ?? 0
                    );
                    setActiveFOLId(folRaw && folRaw > 0 ? folRaw : null);
                    setSizes([65, 35]);
                  } catch {}
                  // Clear tracking fields and selected files to prep for next label
                  setLabelTracks({});
                  setLabelTracking('');
                  setLabelFiles([]);
                  setLabelOpen(false);
                  // Toast success
                  try {
                    showToast({ type: 'success', message: `${labelIds.length} label${labelIds.length>1?'s':''} attached` });
                  } catch {}
                } catch (e) {
                  alert(e.message || 'Failed to add label');
                  try { showToast({ type: 'error', message: e.message || 'Failed to attach label(s)' }); } catch {}
                } finally {
                  setLabelBusy(false);
                }
              }}>
                {labelBusy ? 'Saving…' : (
                  <>
                    <FaPaperclip style={{ marginRight: 6 }} />
                    {labelFiles.length > 1 ? 'Attach Label(s)' : 'Attach Label'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {editQtyOpen && (
        <div className="modal-overlay" onClick={closeEditQtyModal}>
          <div
            className="modal"
            style={{ width: '50%', maxWidth: 960 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-head">
              <h3>Edit Line Quantities</h3>
              <button className="mini-btn" onClick={closeEditQtyModal} aria-label="Close" disabled={editQtyBusy}>×</button>
            </div>
            <div className="modal-content" style={{ maxHeight: '60vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {editQtyError && (
                <div className="error-banner">
                  {editQtyError}
                </div>
              )}
              <table className="modal-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Line #</th>
                    <th style={{ textAlign: 'left' }}>SKU</th>
                    <th style={{ textAlign: 'right' }}>Ordered</th>
                    <th style={{ textAlign: 'right' }}>Allocated</th>
                    <th style={{ textAlign: 'right' }}>New Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {editQtyRows.map(row => (
                    <tr key={row.id}>
                      <td style={{ textAlign: 'left' }}>#{row.id}</td>
                      <td style={{ textAlign: 'left' }}>{row.sku || '(no sku)'}</td>
                      <td style={{ textAlign: 'right' }}>{row.qty_ordered ?? 0}</td>
                      <td style={{ textAlign: 'right' }}>{row.qty_allocated ?? 0}</td>
                      <td style={{ textAlign: 'right' }}>
                        <input
                          type="number"
                          min={0}
                          className="neo-input"
                          style={{ width: 120 }}
                          value={editQtyValues[row.id] ?? ''}
                          onChange={(e) => setEditQtyValues(prev => ({ ...prev, [row.id]: e.target.value }))}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span>Reason (optional)</span>
                <textarea
                  className="neo-input"
                  rows={3}
                  value={editQtyReason}
                  onChange={(e) => setEditQtyReason(e.target.value)}
                  placeholder="Why are you changing the ordered quantity?"
                />
              </label>
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={closeEditQtyModal} disabled={editQtyBusy}>Cancel</button>
              <button className="btn primary" onClick={saveEditedQty} disabled={editQtyBusy}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
      {/* Replace SKU Modal */}
      {swapOpen && (
        <div className="sku-modal-overlay" onClick={(e) => {
          if (e.target.className === 'sku-modal-overlay') cancelSkuSwap();
        }}>
          <div
            className="sku-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sku-modal-title"
            tabIndex="-1"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 3400,
            }}
            onKeyDown={(e) => {
              if (e.key === 'Tab') {
                const focusable = e.currentTarget.querySelectorAll('button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                  e.preventDefault();
                  last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                  e.preventDefault();
                  first.focus();
                }
              }
            }}
          >
            <div className="sku-head">
              <h3 id="sku-modal-title" className="sku-title">
                Replace SKU{swapFor?.sku ? ` (current: ${swapFor.sku})` : ''}
                {Number(swapFor?.qty_allocated || 0) > 0 && (
                  <span className="sku-sub"> ({swapFor.qty_allocated} allocated will be returned to inventory)</span>
                )}
              </h3>
              <button className="mini-btn" onClick={cancelSkuSwap} aria-label="Close">×</button>
            </div>
            <div className="sku-body">
              <div className="sku-tools">
                <div className="sku-searchbar">
                  <input
                    placeholder="Search available inventory by SKU, description…"
                    value={skuQ}
                    onChange={(e) => setSkuQ(e.target.value)}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter') await runSkuSearch(skuQ);
                    }}
                  />
                </div>
                <button className="btn" onClick={() => runSkuSearch(skuQ)} disabled={skuLoading}>
                  {skuLoading ? 'Searching…' : 'Search'}
                </button>
              </div>
              <div className="table-wrapper">
                <table className="sku-table">
                  <thead>
                    <tr>
                      <th>Pick</th>
                      <th>SKU</th>
                      <th>Description</th>
                      <th>On Hand</th>
                      <th>Receipt #</th>
                      <th>Location</th>
                      <th>Storage Unit #</th>
                      <th>Location Type</th>
                      <th>Image</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(skuResults) && skuResults.length ? skuResults.map((r, idx) => {
                      const thumbSrc = resolveItemThumb(r, blobUrlBag);
                      return (
                        <tr
                          key={idx}
                          className={skuSelected?.sku === r.sku ? 'selected' : ''}
                          onClick={() => setSkuSelected(r)}
                        >
                          <td>
                            <input
                              type="radio"
                              name="skuPick"
                              checked={skuSelected?.sku === r.sku}
                              onChange={() => setSkuSelected(r)}
                            />
                          </td>
                          <td className="nowrap">{r.sku}</td>
                          <td>{r.description || r.itemdescription || ''}</td>
                          <td className="num">{formatOnHand(r)}</td>
                          <td className="nowrap">{r.receiptid ?? '—'}</td>
                          <td>{r.location || r.areaname || 'Floor'}</td>
                          <td className="nowrap">
                            {r.pallet_number ?? r.pallet ?? '—'}
                          </td>
                          <td className="nowrap">{fmtLocationType(r)}</td>
                          <td>
                            {thumbSrc ? (
                              <img
                                src={thumbSrc}
                                alt={r.description || r.sku || 'thumbnail'}
                                className="sku-thumb"
                                onMouseEnter={(e) => showPreview(r.image1 || r.itemimageurl, e)}
                                onMouseMove={movePreview}
                                onMouseLeave={hidePreview}
                              />
                            ) : (
                              <span style={{ opacity: 0.6 }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    }) : (
                      <tr><td colSpan={9} style={{ opacity: .7 }}>{skuLoading ? 'Loading…' : 'No results'}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {swapErr && <div className="sku-replace-note error">{swapErr}</div>}
              {Number(swapFor?.qty_allocated || 0) > 0 && (
                <div className="sku-replace-note info">
                  Note: {swapFor.qty_allocated} allocated item(s) will be returned to inventory for SKU {swapFor.sku}.
                </div>
              )}
            </div>
            <div className="sku-foot">
              <div className="left">
                {skuSelected ? `Selected: ${skuSelected.sku}` : 'Select a SKU to replace'}
              </div>
              <div className="right">
                <button className="btn" onClick={cancelSkuSwap}>Cancel</button>
                <button
                  className="btn primary"
                  onClick={confirmSkuSwap}
                  disabled={!skuSelected || (IS_CLIENT && String(orderStatusName || '').toUpperCase() !== 'NEW')}
                  title={IS_CLIENT && String(orderStatusName || '').toUpperCase() !== 'NEW' ? 'Clients can only replace SKU when order status is NEW.' : undefined}
                >
                  Replace SKU
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
